import { Button, ButtonSize, ButtonVariant, Show } from "shared/components";
import type { UnderwritingAgentStatus } from "api/underwriting-agents/types";
import { IconAI } from "shared/components/ai/IconAI";
import { cx } from "shared/utils";
import { getReviewProgress } from "./reviewStatus";

const RING_RADIUS = 10;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

/** The agent's circle in a light pink ring that fills as reports are reviewed; it pulses while the agent works. */
const AgentProgressRing = ({
  fraction,
  reviewing
}: {
  fraction: number;
  reviewing: boolean;
}) => (
  <span className="relative flex size-6 shrink-0 items-center justify-center">
    <svg
      width={24}
      height={24}
      viewBox="0 0 24 24"
      className="absolute inset-0 -rotate-90"
    >
      <circle
        cx={12}
        cy={12}
        r={RING_RADIUS}
        fill="none"
        strokeWidth={2}
        className="stroke-alpha-100"
      />
      <circle
        cx={12}
        cy={12}
        r={RING_RADIUS}
        fill="none"
        strokeWidth={2}
        strokeLinecap="round"
        strokeDasharray={RING_LENGTH}
        strokeDashoffset={RING_LENGTH * (1 - fraction)}
        className="stroke-primary-300 transition-all"
      />
    </svg>
    <span className={cx("relative flex", reviewing && "animate-pulse")}>
      <IconAI bgSize={14} />
    </span>
  </span>
);

/** Opens the underwriting agent's findings; the ring and tooltip say how far the agent is and what it couldn't review. */
export const AgentReviewButton = ({
  status,
  onClick
}: {
  status: UnderwritingAgentStatus;
  onClick: () => void;
}) => {
  const { reviewing, fraction, toReview, tooltip } = getReviewProgress(status);

  return (
    <Button
      variant={ButtonVariant.BASE}
      size={ButtonSize.SM}
      onClick={onClick}
      tooltip={
        <span className="block max-w-[260px] whitespace-normal">{tooltip}</span>
      }
      tooltipPlacement="bottom"
      tooltipZIndex={21}
    >
      <div className="flex items-center gap-2.5">
        <AgentProgressRing fraction={fraction} reviewing={reviewing} />
        <span>Agent review</span>
        <Show if={toReview > 0}>
          <span className="flex size-4 min-w-4 items-center justify-center rounded-[4px] bg-primary-500 px-0.5 text-[10px] font-medium leading-4 text-white">
            {toReview}
          </span>
        </Show>
      </div>
    </Button>
  );
};
